import { createQuery, defineQuery, QueryableResults } from "@nova/queryable"
import { usePilotTrackingQuery } from "../PilotTrackingQueries"
import { MemberExtended, MissionLogsHeatmapPoint } from "@nova/schemas"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import useSWR from "swr"
import { selectAllRegisteredMembers } from "features/entity-caching/members/UserManagementSlice"

const query = createQuery({
    resource: "missions",
    select: ["user_id"],
    distinctOn: "user_id",
    filters: [{ field: "user_id", op: "IS NOT NULL", value: null }],
})

const filterUser = (search: string, user: MemberExtended) => {
    search = search.toLowerCase()
    return (
        !search ||
        user.fullName?.toLowerCase().includes(search) ||
        user.givenName?.toLowerCase().includes(search) ||
        user.familyName?.toLowerCase().includes(search)
    )
}

export const usePilots = ({ search = "" }: { search?: string } = {}) => {
    const { data: pilotTrackingData, isLoading: isPilotTrackingLoading } =
        usePilotTrackingQuery(query)
    const _users = useAppSelector(selectAllRegisteredMembers)

    const pilots = _users?.filter((user) => filterUser(search, user))
    const activeUserIds = new Set(
        pilotTrackingData?.map((d) => d.user_id).filter((id): id is string => id != null)
    )

    return { pilots, activeUserIds, isLoading: isPilotTrackingLoading }
}

export const certificatesQ = defineQuery((pilotId: string) => ({
    resource: "user_certificates",
    select: [
        "id",
        "name",
        "certificate_number",
        "created_at",
        "expires_at",
        "notes",
        "org_id",
        "file_name",
        "issued_at",
        "expiry_config",
        "updated_at",
        "user_id",
        "file_size_bytes",
    ],
    filters: [{ field: "user_id", op: "=", value: pilotId }],
    distinctOn: "id",
}))

export type CertificateQueryResponse = QueryableResults<typeof certificatesQ>

// Sentinel used for prev-period queries when there is no prior period (e.g. "all time").
// The impossible range ensures zero results without changing the return type.
const NO_PREV_PERIOD = "2100-01-01T00:00:00.000Z"

export function getPilotOverviewQueries({
    pilotId,
    currentRangeStart,
    previousRangeStart,
}: {
    pilotId: string
    currentRangeStart: string | null
    previousRangeStart: string | null
}) {
    const currentDateFilter = currentRangeStart
        ? ([{ field: "started_at", op: ">=", value: currentRangeStart }] as const)
        : []

    const prevStart = previousRangeStart ?? NO_PREV_PERIOD
    const prevEnd = currentRangeStart ?? NO_PREV_PERIOD

    return {
        currentTimeRangeFlights: createQuery({
            resource: "metadata",
            aggregates: [{ field: "mission_id", func: "COUNT", alias: "currentFlights" }],
            filters: [{ field: "mission.user_id", op: "=", value: pilotId }, ...currentDateFilter],
        }),
        prevTimeRangeFlights: createQuery({
            resource: "metadata",
            aggregates: [{ field: "mission_id", func: "COUNT", alias: "prevFlights" }],
            filters: [
                { field: "mission.user_id", op: "=", value: pilotId },
                { field: "started_at", op: ">=", value: prevStart },
                { field: "started_at", op: "<", value: prevEnd },
            ],
        }),

        currentTimeRangeHours: createQuery({
            resource: "metadata",
            aggregates: [{ field: "airtime_hours", func: "SUM", alias: "currentHours" }],
            filters: [{ field: "mission.user_id", op: "=", value: pilotId }, ...currentDateFilter],
        }),
        prevTimeRangeHours: createQuery({
            resource: "metadata",
            aggregates: [{ field: "airtime_hours", func: "SUM", alias: "prevHours" }],
            filters: [
                { field: "mission.user_id", op: "=", value: pilotId },
                { field: "started_at", op: ">=", value: prevStart },
                { field: "started_at", op: "<", value: prevEnd },
            ],
        }),

        currentTimeRangeDistance: createQuery({
            resource: "metadata",
            aggregates: [{ field: "distance_flown", func: "SUM", alias: "currentDistance" }],
            filters: [{ field: "mission.user_id", op: "=", value: pilotId }, ...currentDateFilter],
        }),
        prevTimeRangeDistance: createQuery({
            resource: "metadata",
            aggregates: [{ field: "distance_flown", func: "SUM", alias: "prevDistance" }],
            filters: [
                { field: "mission.user_id", op: "=", value: pilotId },
                { field: "started_at", op: ">=", value: prevStart },
                { field: "started_at", op: "<", value: prevEnd },
            ],
        }),

        currentTimeRangeFlightsGraph: createQuery({
            resource: "missions",
            timeBuckets: [{ field: "metadata.started_at", unit: "day", alias: "bucket" }],
            aggregates: [{ func: "COUNT", field: "id", alias: "totalFlightsGraph" }],
            filters: [
                { field: "user_id", op: "=", value: pilotId },
                ...(currentRangeStart
                    ? ([
                          { field: "metadata.started_at", op: ">=", value: currentRangeStart },
                      ] as const)
                    : []),
            ],
        }),
        currentTimeRangeHoursGraph: createQuery({
            resource: "metadata",
            timeBuckets: [{ field: "started_at", unit: "day", alias: "bucket" }],
            aggregates: [{ func: "SUM", field: "airtime", alias: "totalHoursGraph" }],
            filters: [{ field: "mission.user_id", op: "=", value: pilotId }, ...currentDateFilter],
        }),
        currentTimeRangeDistanceFlownGraph: createQuery({
            resource: "metadata",
            timeBuckets: [{ field: "started_at", unit: "day", alias: "bucket" }],
            aggregates: [{ func: "SUM", field: "distance_flown", alias: "distanceFlownGraph" }],
            filters: [{ field: "mission.user_id", op: "=", value: pilotId }, ...currentDateFilter],
        }),

        pilotTopAircraft: createQuery({
            resource: "metadata",
            select: [
                "mission.aircraft.id",
                "mission.aircraft.model",
                "mission.aircraft.manufacturer",
                "mission.aircraft.nickname",
            ],
            filters: [{ field: "mission.user_id", op: "=", value: pilotId }],
            aggregates: [{ field: "airtime_hours", func: "SUM", alias: "pilotTopAircraftHours" }],
            orderBy: [{ dir: "DESC", field: "pilotTopAircraftHours" }],
            limit: 1,
        }),
        pilotAllTimeHours: createQuery({
            resource: "metadata",
            aggregates: [{ field: "airtime_hours", func: "SUM", alias: "pilotAllTimeHours" }],
            filters: [{ field: "mission.user_id", op: "=", value: pilotId }],
        }),

        currentTimeRangeFlightsPodium: createQuery({
            resource: "missions",
            select: ["user_id"],
            aggregates: [{ field: "id", func: "COUNT", alias: "numMissions" }],
            orderBy: [{ dir: "DESC", field: "numMissions" }],
            ...(currentRangeStart
                ? {
                      filters: [
                          { field: "metadata.started_at", op: ">=", value: currentRangeStart },
                      ],
                  }
                : {}),
        }),
        currentTimeRangeHoursPodium: createQuery({
            resource: "metadata",
            select: ["mission.user_id"],
            aggregates: [{ field: "airtime", func: "SUM", alias: "totalAirtime" }],
            orderBy: [{ dir: "DESC", field: "totalAirtime" }],
            ...(currentRangeStart
                ? { filters: [{ field: "started_at", op: ">=", value: currentRangeStart }] }
                : {}),
        }),
        currentTimeRangeDistanceFlownPodium: createQuery({
            resource: "metadata",
            select: ["mission.user_id"],
            aggregates: [{ field: "distance_flown", func: "SUM", alias: "distanceFlown" }],
            orderBy: [{ dir: "DESC", field: "distanceFlown" }],
            ...(currentRangeStart
                ? { filters: [{ field: "started_at", op: ">=", value: currentRangeStart }] }
                : {}),
        }),

        certificates: certificatesQ(pilotId),
    }
}

export const useMissionLogsHeatmap = ({
    userId,
    from,
    to,
    precision,
}: {
    userId: string
    from?: string
    to?: string
    precision?: number
}): { data: MissionLogsHeatmapPoint[] | undefined; isLoading: boolean } => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const queryString = new URLSearchParams({
        userId,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(precision !== undefined ? { precision: String(precision) } : {}),
    }).toString()

    const { data, isLoading } = useSWR(
        orgId ? ["mission-logs-heatmap", orgId, userId, from, to, precision] : null,
        () =>
            fetchApiGateway("get /org/:orgId/fleet/missions/heatmap", {
                params: { orgId: orgId! },
                query: queryString,
            }) as Promise<MissionLogsHeatmapPoint[]>
    )

    return { data, isLoading }
}
